import 'whatwg-fetch';

import {
	AppDispatch,
	RootState,
} from '@/store/store';

import type { ApiErrorResp } from '@/types/endpoints/internal/resp/ApiErrorResp';
import { CheckoutPlaceOrderProps } from '@/types/endpoints/internal/checkout/CheckoutPlaceOrderProps';
import type {
	CheckoutStepDataPayload,
	PaymentMethodApiReqProps,
	PaymentMethodUpdateProps,
	CheckoutTransbankInitiateProps,
	CheckoutPlaceBuylistOrderProps,
	CheckoutAdminPlaceBuylistOrderProps,
	CheckoutMethodAckResp,
	CheckoutPlaceOrderResp,
} from '@/types/endpoints/internal/checkout/CheckoutActionProps';

import {
	ACTION_CHECKOUT_SET_STEP_DATA,
	ACTION_CHECKOUT_CLEAR_DATA,
} from '@/constants/actions';
import {
	ENDPOINT_CHECKOUT_GET_ADMIN_SHIPPING_METHODS,
	ENDPOINT_CHECKOUT_GET_SHIPPING_METHODS,
	ENDPOINT_CHECKOUT_ADD_SHIPPING_METHOD,
	ENDPOINT_CHECKOUT_UPDATE_SHIPPING_METHOD,
	ENDPOINT_CHECKOUT_DELETE_SHIPPING_METHOD,
	ENDPOINT_CHECKOUT_FETCH_SINGLE_SHIPPING_METHOD,
	ENDPOINT_CHECKOUT_FETCH_PAYMENT_METHODS,
	ENDPOINT_CHECKOUT_FETCH_BUYLIST_PAYMENT_METHODS,
	ENDPOINT_CHECKOUT_FETCH_ADMIN_PAYMENT_METHODS,
	ENDPOINT_CHECKOUT_ADD_PAYMENT_METHOD,
	ENDPOINT_CHECKOUT_UPDATE_PAYMENT_METHOD,
	ENDPOINT_CHECKOUT_DELETE_PAYMENT_METHOD,
	ENDPOINT_CHECKOUT_FETCH_SINGLE_PAYMENT_METHOD,
	ENDPOINT_PAYMENT_TRANSBANK_INITIATE_PAYMENT,
	ENDPOINT_ORDERS_PLACE_ORDER,
	ENDPOINT_ORDERS_PLACE_BUYLIST_ORDER,
	ENDPOINT_ORDERS_ADMIN_PLACE_BUYLIST_ORDER,
} from '@/constants/endpoints';

import {
	PaymentMethod,
	PaymentRecord,
} from '@/models/payments';
import type { PaymentMethodApiRespProps } from '@/models/payments/PaymentMethod';
import type { PaymentRecordApiRespProps } from '@/models/payments/PaymentRecord';
import ShippingMethod, { ShippingMethodApiReqProps, ShippingMethodApiRespProps } from '@/models/shipping/ShippingMethod';

import { stringFormat } from '@/utils/formatting';
import {
	fetchTitan,
	getUrlParams,
} from '@/utils/request';


/******************************
*******  Synchronous  *********
******************************/



export function checkoutSetStepData(payload: CheckoutStepDataPayload) {
	return {
		type: ACTION_CHECKOUT_SET_STEP_DATA,
		payload
	};
}

export function checkoutClearStepData() {
	return {
		type: ACTION_CHECKOUT_CLEAR_DATA,
	};
}





/******************************
*******  Asynchronous  ********
******************************/



export function checkoutFetchAdminShippingMethods(
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<ShippingMethod[]> => {

		const endpoint = ENDPOINT_CHECKOUT_GET_ADMIN_SHIPPING_METHODS;
		const options = {
			method: 'get',
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: ShippingMethodApiRespProps[]) => {
				return Promise.resolve(resp.map((sm) => new ShippingMethod(sm)));
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}

export function checkoutFetchShippingMethods(
	shippingAddressUuid: string | null = null,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<ShippingMethod[]> => {

		const getData = shippingAddressUuid ? { "shipping_address_uuid": shippingAddressUuid } : {};

		const endpoint = ENDPOINT_CHECKOUT_GET_SHIPPING_METHODS + getUrlParams(getData);
		const options = {
			method: 'get',
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: ShippingMethodApiRespProps[]) => {
				return Promise.resolve(resp.map((sm) => new ShippingMethod(sm)));
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}

export function checkoutAddShippingMethod(
	addData: ShippingMethodApiReqProps,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<CheckoutMethodAckResp> => {

		const endpoint = ENDPOINT_CHECKOUT_ADD_SHIPPING_METHOD;
		const options = {
			method: 'post',
			body: JSON.stringify(addData),
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: CheckoutMethodAckResp) => {
				return Promise.resolve(resp);
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}

export function checkoutUpdateShippingMethod(
	editData: ShippingMethodApiReqProps,
	uuid: string,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<CheckoutMethodAckResp> => {

		const endpoint = stringFormat(ENDPOINT_CHECKOUT_UPDATE_SHIPPING_METHOD, { uuid: uuid });
		const options = {
			method: 'put',
			body: JSON.stringify(editData),
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: CheckoutMethodAckResp) => {
				return Promise.resolve(resp);
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}

export function checkoutShippingMethodDelete(
	uuid: string,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<CheckoutMethodAckResp> => {

		const endpoint = stringFormat(ENDPOINT_CHECKOUT_DELETE_SHIPPING_METHOD, { uuid: uuid });
		const options = {
			method: 'delete',
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: CheckoutMethodAckResp) => {
				return Promise.resolve(resp);
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutFetchSingleShippingMethods(
	uuid: string,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<ShippingMethod> => {

		const endpoint = stringFormat(ENDPOINT_CHECKOUT_FETCH_SINGLE_SHIPPING_METHOD, { uuid: uuid });
		const options = {
			method: 'get',
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: ShippingMethodApiRespProps) => {
				return Promise.resolve(new ShippingMethod(resp));
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutFetchPaymentMethods(
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<PaymentMethod[]> => {

		const endpoint = ENDPOINT_CHECKOUT_FETCH_PAYMENT_METHODS;
		const options = {
			method: 'get',
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: PaymentMethodApiRespProps[]) => {
				return Promise.resolve(resp.map((pm) => new PaymentMethod(pm)));
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutFetchBuylistPaymentMethods(
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<PaymentMethod[]> => {

		const endpoint = ENDPOINT_CHECKOUT_FETCH_BUYLIST_PAYMENT_METHODS;
		const options = {
			method: 'get',
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: PaymentMethodApiRespProps[]) => {
				return Promise.resolve(resp.map((pm) => new PaymentMethod(pm)));
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutFetchAdminPaymentMethods(
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<PaymentMethod[]> => {

		const endpoint = ENDPOINT_CHECKOUT_FETCH_ADMIN_PAYMENT_METHODS;
		const options = {
			method: 'get',
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: PaymentMethodApiRespProps[]) => {
				return Promise.resolve(resp.map((pm) => new PaymentMethod(pm)));
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutAddPaymentMethod(
	addData: PaymentMethodApiReqProps,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<PaymentMethodApiRespProps> => {

		const endpoint = ENDPOINT_CHECKOUT_ADD_PAYMENT_METHOD;
		const options = {
			method: 'post',
			body: JSON.stringify(addData),
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: PaymentMethodApiRespProps) => {
				return Promise.resolve(resp);
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutUpdatePaymentMethod(
	editData: PaymentMethodUpdateProps,
	uuid: string,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<CheckoutMethodAckResp> => {

		const endpoint = stringFormat(ENDPOINT_CHECKOUT_UPDATE_PAYMENT_METHOD, { uuid: uuid });
		const options = {
			method: 'put',
			body: JSON.stringify(editData),
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: CheckoutMethodAckResp) => {
				return Promise.resolve(resp);
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutPaymentMethodDelete(
	uuid: string,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<CheckoutMethodAckResp> => {

		const endpoint = stringFormat(ENDPOINT_CHECKOUT_DELETE_PAYMENT_METHOD, { uuid: uuid });
		const options = {
			method: 'delete',
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: CheckoutMethodAckResp) => {
				return Promise.resolve(resp);
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutFetchSinglePaymentMethod(
	uuid: string,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<PaymentMethod> => {

		const endpoint = stringFormat(ENDPOINT_CHECKOUT_FETCH_SINGLE_PAYMENT_METHOD, { uuid: uuid });
		const options = {
			method: 'get',
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: PaymentMethodApiRespProps) => {
				return Promise.resolve(new PaymentMethod(resp));
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutInitiatePaymentTransbank(
	paymentData: CheckoutTransbankInitiateProps,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<PaymentRecord> => {

		const endpoint = ENDPOINT_PAYMENT_TRANSBANK_INITIATE_PAYMENT;
		const options = {
			method: 'post',
			body: JSON.stringify(paymentData),
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: PaymentRecordApiRespProps) => {
				return Promise.resolve(new PaymentRecord(resp));
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutPlaceOrder(
	orderData: CheckoutPlaceOrderProps,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<CheckoutPlaceOrderResp> => {

		const endpoint = ENDPOINT_ORDERS_PLACE_ORDER;
		const options = {
			method: 'post',
			body: JSON.stringify(orderData),
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: CheckoutPlaceOrderResp) => {
				return Promise.resolve(resp);
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutPlaceBuylistOrder(
	orderData: CheckoutPlaceBuylistOrderProps,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<CheckoutPlaceOrderResp> => {

		const endpoint = ENDPOINT_ORDERS_PLACE_BUYLIST_ORDER;
		const options = {
			method: 'post',
			body: JSON.stringify(orderData),
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: CheckoutPlaceOrderResp) => {
				return Promise.resolve(resp);
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}


export function checkoutAdminPlaceBuylistOrder(
	orderData: CheckoutAdminPlaceBuylistOrderProps,
	signal: AbortSignal | null = null
) {
	return async (dispatch: AppDispatch, getState: () => RootState): Promise<CheckoutPlaceOrderResp> => {

		const endpoint = ENDPOINT_ORDERS_ADMIN_PLACE_BUYLIST_ORDER;
		const options = {
			method: 'post',
			body: JSON.stringify(orderData),
			signal,
		};

		return fetchTitan(dispatch, getState, endpoint, options)
			.then((resp: CheckoutPlaceOrderResp) => {
				return Promise.resolve(resp);
			})
			.catch((errResp: ApiErrorResp) => {
				return Promise.reject(errResp);
			});
	};
}
